import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Select from 'react-select'
import Swal from 'sweetalert2'
import httpClient from 'utility/httpClient.util'
import { WebSocketContext } from 'contexts/WebSocketProvider'
import './CodexAgentFloatingMenu.css'

const projectOptions = [
  { value: 'fe', label: 'Frontend' },
  { value: 'be', label: 'Backend' },
]

const sandboxOptions = [
  { value: 'read-only', label: 'Read only' },
  { value: 'workspace-write', label: 'Workspace write' },
]

const MAX_STREAM_EVENTS = 80

const getEventPayload = (event) => event?.data || event || {}

const getStreamMessage = (payload) => {
  if (payload.message) return payload.message
  if (payload.eventType === 'thread_started') return 'เริ่ม Codex thread'
  if (payload.eventType === 'turn_started') return 'เริ่มประมวลผลงาน'
  if (payload.eventType === 'turn_completed') return 'ประมวลผลงานเสร็จ'
  return payload.eventType || payload.rawType || 'codex event'
}

const safeJsonStringify = (value) => {
  if (typeof value === 'undefined' || value === null || value === '') return ''
  if (typeof value === 'string') return value

  try {
    return JSON.stringify(value, null, 2)
  } catch (error) {
    return `${value}`
  }
}

const getContextPayloadValue = (contextPayload) =>
  typeof contextPayload === 'function' ? contextPayload() : contextPayload

const buildCodexPrompt = ({ contextPayload, prompt, systemPrompt }) => {
  const sections = []
  const systemText = `${systemPrompt || ''}`.trim()
  const contextValue = getContextPayloadValue(contextPayload)
  const contextText = safeJsonStringify(contextValue)

  if (systemText) {
    sections.push(`System prompt:\n${systemText}`)
  }

  if (contextText) {
    sections.push(`Context payload:\n\`\`\`json\n${contextText}\n\`\`\``)
  }

  sections.push(`User prompt:\n${prompt}`)

  return sections.join('\n\n')
}

const parseJsonValue = (value) => {
  try {
    return { ok: true, value: JSON.parse(value) }
  } catch (error) {
    return { ok: false }
  }
}

const findJsonCandidateEnd = (text, startIndex) => {
  const stack = []
  let inString = false
  let escaping = false

  for (let index = startIndex; index < text.length; index += 1) {
    const char = text[index]

    if (inString) {
      if (escaping) {
        escaping = false
      } else if (char === '\\') {
        escaping = true
      } else if (char === '"') {
        inString = false
      }
      continue
    }

    if (char === '"') {
      inString = true
      continue
    }

    if (char === '{') {
      stack.push('}')
      continue
    }

    if (char === '[') {
      stack.push(']')
      continue
    }

    if (char === '}' || char === ']') {
      if (stack.pop() !== char) return -1
      if (stack.length === 0) return index
    }
  }

  return -1
}

const extractJsonArtifact = (message = '') => {
  const text = `${message || ''}`.trim()
  if (!text) return null

  const fencedBlockRegex = /```(?:json)?\s*([\s\S]*?)```/gi
  let fencedBlock = fencedBlockRegex.exec(text)

  while (fencedBlock) {
    const raw = `${fencedBlock[1] || ''}`.trim()
    const parsed = parseJsonValue(raw)
    if (parsed.ok) return { raw, value: parsed.value }
    fencedBlock = fencedBlockRegex.exec(text)
  }

  const directParsed = parseJsonValue(text)
  if (directParsed.ok) return { raw: text, value: directParsed.value }

  for (let index = 0; index < text.length; index += 1) {
    if (text[index] !== '{' && text[index] !== '[') continue
    const endIndex = findJsonCandidateEnd(text, index)
    if (endIndex < 0) continue
    const raw = text.slice(index, endIndex + 1)
    const parsed = parseJsonValue(raw)
    if (parsed.ok) return { raw, value: parsed.value }
  }

  return null
}

const TooltipIconButton = ({ tooltip, className = '', children, ...props }) => (
  <OverlayTrigger placement="left" overlay={<Tooltip>{tooltip}</Tooltip>}>
    <button
      type="button"
      className={className}
      aria-label={tooltip}
      {...props}
    >
      {children}
    </button>
  </OverlayTrigger>
)

const CodexAgentFloatingMenu = ({
  contextPayload = null,
  defaultProject = 'fe',
  onFinalMessage,
  onJsonArtifact,
  panelTitle = 'Codex Agent',
  promptPlaceholder = '',
  systemPrompt = '',
}) => {
  const socketContext = useContext(WebSocketContext) || {}
  const { connected, subscribe, unsubscribe } = socketContext
  const [expanded, setExpanded] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [project, setProject] = useState(
    projectOptions.find((option) => option.value === defaultProject) ||
      projectOptions[0],
  )
  const [sandbox, setSandbox] = useState(sandboxOptions[0])
  const [conversationId, setConversationId] = useState(null)
  const [jobId, setJobId] = useState(null)
  const [jobStatus, setJobStatus] = useState('idle')
  const [streamEvents, setStreamEvents] = useState([])
  const [finalMessage, setFinalMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const lastFinalMessageRef = useRef('')

  const roomName = useMemo(
    () => (conversationId ? `codex-agent-${conversationId}` : null),
    [conversationId],
  )

  const notifyFinalMessage = useCallback((message, meta = {}) => {
    const text = `${message || ''}`.trim()
    if (!text) return

    const finalMessageKey = `${meta.jobId || jobId || ''}:${text}`
    if (lastFinalMessageRef.current === finalMessageKey) return
    lastFinalMessageRef.current = finalMessageKey

    if (onFinalMessage) {
      try {
        Promise.resolve(onFinalMessage(text, meta)).catch((error) => {
          console.warn('CodexAgentFloatingMenu onFinalMessage failed:', error)
        })
      } catch (error) {
        console.warn('CodexAgentFloatingMenu onFinalMessage failed:', error)
      }
    }

    if (onJsonArtifact) {
      const artifact = extractJsonArtifact(text)
      if (artifact) {
        try {
          Promise.resolve(onJsonArtifact(artifact.value, {
            ...meta,
            finalMessage: text,
            rawJson: artifact.raw,
          })).catch((error) => {
            console.warn('CodexAgentFloatingMenu onJsonArtifact failed:', error)
          })
        } catch (error) {
          console.warn('CodexAgentFloatingMenu onJsonArtifact failed:', error)
        }
      }
    }
  }, [jobId, onFinalMessage, onJsonArtifact])

  useEffect(() => {
    if (!roomName || !connected || !subscribe) return undefined

    const channel = subscribe(roomName)
    channel.bind('codex-agent:job-stream', (event) => {
      const payload = getEventPayload(event)
      setStreamEvents((prev) =>
        [
          ...prev,
          {
            ...payload,
            message: getStreamMessage(payload),
          },
        ].slice(-MAX_STREAM_EVENTS),
      )
    })
    channel.bind('codex-agent:job', (event) => {
      const payload = getEventPayload(event)
      if (payload.status) setJobStatus(payload.status)
      if (payload.finalMessage) {
        setFinalMessage(payload.finalMessage)
        notifyFinalMessage(payload.finalMessage, {
          ...payload,
          source: 'socket',
        })
      }
      if (payload.jobId) setJobId(payload.jobId)
    })

    return () => {
      unsubscribe && unsubscribe(roomName)
    }
  }, [roomName, connected, subscribe, unsubscribe, notifyFinalMessage])

  const startNewConversation = () => {
    setConversationId(null)
    setJobId(null)
    setJobStatus('idle')
    setStreamEvents([])
    setFinalMessage('')
    lastFinalMessageRef.current = ''
  }

  const loadJobEvents = useCallback(async (targetJobId = jobId) => {
    if (!targetJobId) return
    const response = await httpClient.get(
      `/designer/codex-agent/jobs/${targetJobId}/events`,
    )
    const list = response?.data?.data || []
    setStreamEvents(
      list.map((item) => ({
        eventId: item.codexAgentJobEventId,
        eventType: item.eventType,
        rawType: item.rawType,
        sequence: item.sequence,
        message: item.message || item.eventType,
        payload: item.payloadJson,
        createdAt: item.createdAt,
      })).slice(-MAX_STREAM_EVENTS),
    )
  }, [jobId])

  const loadLatestJob = useCallback(async (targetJobId = jobId) => {
    if (!targetJobId) return
    try {
      const response = await httpClient.get(
        `/designer/codex-agent/jobs/${targetJobId}`,
      )
      const data = response?.data?.data || {}
      setJobStatus(data.status || 'idle')
      setFinalMessage(data.finalMessage || '')
      notifyFinalMessage(data.finalMessage, {
        conversationId,
        jobId: targetJobId,
        source: 'polling',
        status: data.status,
        threadId: data.threadId,
      })
      await loadJobEvents(targetJobId)
    } catch (error) {
      Swal.fire('โหลดผล Codex ไม่สำเร็จ', '', 'error')
    }
  }, [conversationId, jobId, loadJobEvents, notifyFinalMessage])

  useEffect(() => {
    if (!expanded || !jobId || !['queued', 'running'].includes(jobStatus)) {
      return undefined
    }

    const timer = setInterval(() => {
      loadLatestJob(jobId)
    }, 5000)

    return () => clearInterval(timer)
  }, [expanded, jobId, jobStatus, loadLatestJob])

  const submitPrompt = async () => {
    const text = prompt.trim()
    if (!text) {
      Swal.fire('โปรดระบุ prompt', '', 'warning')
      return
    }

    setIsSubmitting(true)
    setJobStatus('queued')
    setFinalMessage('')
    setStreamEvents([])

    try {
      const codexPrompt = buildCodexPrompt({
        contextPayload,
        prompt: text,
        systemPrompt,
      })
      const body = {
        title: text.slice(0, 120),
        project: project.value,
        cwdKey: project.value,
        sandbox: sandbox.value,
        prompt: codexPrompt,
      }
      const response = conversationId
        ? await httpClient.post(
            `/designer/codex-agent/conversations/${conversationId}/jobs`,
            {
              sandbox: sandbox.value,
              prompt: codexPrompt,
            },
          )
        : await httpClient.post('/designer/codex-agent/conversations', body)

      const data = response?.data?.data || {}
      const nextJobId = data.job?.codexAgentJobId || null
      setConversationId(data.conversationId || conversationId)
      setJobId(nextJobId)
      setJobStatus(data.job?.status || 'queued')
      setPrompt('')
      if (nextJobId) {
        setTimeout(() => loadLatestJob(nextJobId), 1200)
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'ส่งงาน Codex ไม่สำเร็จ'
      setJobStatus('failed')
      Swal.fire('Codex Agent', message, 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!expanded) {
    return (
      <div className="codex-agent-floating">
        <TooltipIconButton
          tooltip="เปิด Codex Agent"
          className="codex-agent-floating__button"
          onClick={() => setExpanded(true)}
        >
          <i className="fas fa-robot" />
        </TooltipIconButton>
      </div>
    )
  }

  return (
    <div className="codex-agent-floating">
      <div className="codex-agent-floating__panel">
        <div className="codex-agent-floating__header">
          <div>
            <div className="codex-agent-floating__title">{panelTitle}</div>
            <div className="codex-agent-floating__status">
              {jobStatus}
              {conversationId ? ` · ${conversationId.slice(0, 8)}` : ''}
            </div>
          </div>
          <div className="d-flex">
            <TooltipIconButton
              tooltip="เริ่มบทสนทนาใหม่"
              className="codex-agent-floating__icon-btn mr-1"
              onClick={startNewConversation}
            >
              <i className="fas fa-plus" />
            </TooltipIconButton>
            <TooltipIconButton
              tooltip="ย่อ Codex Agent"
              className="codex-agent-floating__icon-btn"
              onClick={() => setExpanded(false)}
            >
              <i className="fas fa-chevron-right" />
            </TooltipIconButton>
          </div>
        </div>
        <div className="codex-agent-floating__body">
          <Form.Group>
            <Form.Label>Project</Form.Label>
            <Select
              classNamePrefix="select"
              options={projectOptions}
              value={project}
              onChange={setProject}
              isDisabled={!!conversationId || isSubmitting}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Sandbox</Form.Label>
            <Select
              classNamePrefix="select"
              options={sandboxOptions}
              value={sandbox}
              onChange={setSandbox}
              isDisabled={isSubmitting}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Prompt</Form.Label>
            <Form.Control
              as="textarea"
              className="codex-agent-floating__textarea"
              value={prompt}
              disabled={isSubmitting}
              placeholder={promptPlaceholder}
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={(event) => {
                if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                  event.preventDefault()
                  submitPrompt()
                }
              }}
            />
          </Form.Group>
          <div className="codex-agent-floating__stream">
            {streamEvents.length === 0 ? (
              <div className="codex-agent-floating__stream-row text-muted">
                {finalMessage || 'ยังไม่มี stream'}
              </div>
            ) : (
              streamEvents.map((event, index) => (
                <div
                  key={`${event.eventId || event.sequence || index}-${index}`}
                  className="codex-agent-floating__stream-row"
                >
                  <span className="codex-agent-floating__stream-meta">
                    #{event.sequence || index + 1} {event.eventType}
                  </span>
                  {event.message}
                </div>
              ))
            )}
          </div>
          {finalMessage && (
            <div className="codex-agent-floating__stream mt-2">
              <div className="codex-agent-floating__stream-row">
                <span className="codex-agent-floating__stream-meta">
                  final
                </span>
                {finalMessage}
              </div>
            </div>
          )}
          <div className="codex-agent-floating__footer">
            <div className="codex-agent-floating__job-link">
              {jobId ? `job: ${jobId}` : 'job: -'}
            </div>
            <div className="d-flex">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>โหลดผลล่าสุด</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mr-1"
                  aria-label="โหลดผลล่าสุด"
                  disabled={!jobId || isSubmitting}
                  onClick={() => loadLatestJob()}
                >
                  <i className="fas fa-sync-alt" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>ส่ง prompt ให้ Codex Agent</Tooltip>}
              >
                <Button
                  variant="primary"
                  size="sm"
                  aria-label="ส่ง prompt ให้ Codex Agent"
                  disabled={isSubmitting}
                  onClick={submitPrompt}
                >
                  <i className="fas fa-paper-plane mr-1" />
                  ส่ง
                </Button>
              </OverlayTrigger>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CodexAgentFloatingMenu
